'use strict';

import { setUserInfo, clearUserInfo } from '../modules/shared/utils/userInfoUtils';

angular.module('app').factory('AuthService', AuthService);

AuthService.$inject = ['$q', 'ApiService', '$location', 'utilService', 'ModalService',
    'constants','$window', '$timeout'];

function AuthService($q, ApiService, $location, utilService, ModalService, constants, $window, $timeout) {
    const authService = {
        corporateSignIn: function (pinCode, corporateAccountInfo) {
            return ApiService.post('api/corporate/cookie-login', {pincode: pinCode}, false, {'corporate-code': corporateAccountInfo.token}).then(function (res) {
                let userInfo = res.data;
                userInfo.corporateAccountInfo = corporateAccountInfo

                authService.setUserInfo(userInfo);

                utilService.goTo('/customers-dashboard');

                return res;
            }, function (err) {
                return err;
            });
        },
        auth0SingIn: function () {
            return ApiService.get('api/oauth/login/auth0').then(function (res) {
                $window.location.href = res.data.redirectUrl;
                return res;
            }, function (err) {
                return err;
            });
        },
        continueAuth0SingIn: function () {
            return ApiService.get('api/user/profile').then(function (res) {
                authService.setUserInfo(res.data).then(() => {
                    $location.search({}); // clear login-by-auth0=true
                    utilService.nextScreen();
                });

                return res;
            }, function (err) {
                return err;
            });
        },
        /**
         * Sign out user and handle navigation.
         * 
         * @param {string|null} redirectTo - Path to redirect after logout. Default is '/' (home/login page).
         *                                   Pass null to stay on current page (page should be prepared to mobx re-render).
         * 
         * hash - /#!/some-page user in angular router
         * 
         * How it works:
         * 1. Calls API to logout (clears server session)
         * 2. Clears user info (MobX stores, localStorage)
         * 3. Navigation:
         *    - If redirectTo is provided: 
         *      * utilService.goTo(redirectTo) - immediately navigates to target page (prevents showing invalid data 
         *        because of MobX re-render without data and reload delay)
         *      * $window.location.replace(`/#!/${redirectTo}`) - reloads page with correct URL hash
         *        (preserves URL structure, unlike $window.location.reload() which keeps current hash)
         *    - If redirectTo is null:
         *      * $window.location.reload() - reloads current page
         * 
         * Why this approach:
         * - utilService.goTo provides immediate navigation, preventing MobX observers from re-rendering
         *   with cleared data and showing errors during the reload delay
         * - $window.location.replace() reloads page with proper URL structure with hash routing
         * - Using null allows staying on current page when page is designed to handle logout state
         * 
         * Why NOT use utilService.goTo() with immediate $window.location.reload():
         * utilService.goTo() redirects to needed page but $window.location.reload() saves hash and redirects to the page it was called from.
         * 
         * Problem flow:
         * /some-page -> signOut() -> utilService.goTo('/new-page') -> $window.location.reload() -> /some-page
         * 
         * Solution:
         * Use $timeout to give AngularJS time to update $location before reload, so reload happens with correct hash.
         */
        signOut: function (redirectTo = '/') {
            return ApiService.get('api/logout').then(function (res) {
                authService.clearUserInfo();

                if (redirectTo) {
                    utilService.goTo(redirectTo);

                    $timeout(function() {
                        $window.location.reload();
                    });
                } else {
                    $window.location.reload();
                }

                return res;
            }, function (err) {
                return err;
            });
        },
        processShowPage(isShowPage) {
            const defer = $q.defer();

            if (isShowPage) {
                defer.resolve();
            } else {
                utilService.nextScreen();
            }

            return defer.promise;
        },
        skipIfLoggedIn() {
            const isUserLoggedIn = !utilService.getLoggedInUserData();
            return this.processShowPage(isUserLoggedIn);
        },
        termsRequired() {
            const user = utilService.getLoggedInUserData();
            const isShowTermsAndCondition = this.getIsShowTermsAndCondition(user);

            return this.processShowPage(isShowTermsAndCondition);
        },
        pinCodeRequired() {
            const isCorporateSignInUnfinished = utilService.getIsCorporateSignInUnfinished();

            return this.processShowPage(isCorporateSignInUnfinished);
        },
        loginRequired: function () {
            const defer = $q.defer();
            const user = utilService.getLoggedInUserData();
            const isCorporateSignInUnfinished = utilService.getIsCorporateSignInUnfinished();
            const isShowTermsAndCondition = this.getIsShowTermsAndCondition(user);
            const selfBillingPaymentIssue = this.getSelfBillingPaymentIssue(user);


            if (!user) { 
                utilService.goTo('/');
            } else if (isCorporateSignInUnfinished) {
                utilService.goTo('/pin-code');
            } else if (isShowTermsAndCondition) {
                utilService.goTo('/terms-and-conditions');
            } else if (selfBillingPaymentIssue) {
                this.processSelfBillingPaymentIssue(selfBillingPaymentIssue);
            } else {
                defer.resolve();
            }

            return defer.promise;
        },
        getIsShowTermsAndCondition(user) {
            if (!user) {
                return false;
            };

            const isCoach = utilService.getIsCoachRole();
            const isEntity = utilService.getIsAdminRole();
            const isNeedTermsAndCondition = isCoach || isEntity;
            const isNewTermsAndCondition = user.readTermsAndConditionVersion !== constants.termsAndConditionVersion;

            return isNewTermsAndCondition && isNeedTermsAndCondition;
        },
        /** return false or selfBillingPaymentStatus */
        getSelfBillingPaymentIssue(user) {
            if (!user || !user.entity) {
                return false;
            };

            const paymentStatus = user.entity.selfBillingEntityPaymentStatus;
            const { ACTIVE } = constants.enums.selfBillingPaymentStatus;

            if (!paymentStatus || paymentStatus === ACTIVE) { 
                return false;
            }

            return paymentStatus;
        },
        processSelfBillingPaymentIssue(paymentStatus) {
            const { CARD_REQUIRED, CARD_AND_PAYMENT_REQUIRED, PAYMENT_REQUIRED } = constants.enums.selfBillingPaymentStatus;

            switch (paymentStatus) {
                case CARD_REQUIRED:
                case CARD_AND_PAYMENT_REQUIRED:
                    utilService.goTo('/self-billing/setup-card');
                    break;
                case PAYMENT_REQUIRED:
                    utilService.goTo('/self-billing/failed-payment');
                    break;
                default:
                    utilService.goTo('/404');
                    console.error('Invalid payment status', paymentStatus);
            }
        },
        setupCardRequired() {
            const user = utilService.getLoggedInUserData();
            const selfBillingPaymentIssue = this.getSelfBillingPaymentIssue(user);

            const { CARD_REQUIRED, CARD_AND_PAYMENT_REQUIRED } = constants.enums.selfBillingPaymentStatus;
            const isShowSetupCard = selfBillingPaymentIssue === CARD_REQUIRED || selfBillingPaymentIssue === CARD_AND_PAYMENT_REQUIRED;

            return this.processShowPage(isShowSetupCard);
        },
        failedPaymentRequired() {
            const user = utilService.getLoggedInUserData();
            const selfBillingPaymentIssue = this.getSelfBillingPaymentIssue(user);

            const { PAYMENT_REQUIRED } = constants.enums.selfBillingPaymentStatus;
            const isShowPaymentRequired = selfBillingPaymentIssue === PAYMENT_REQUIRED;

            return this.processShowPage(isShowPaymentRequired);
        },
        parentToChildLogin: function (adminId) {
            const params = {adminId};
            return ApiService.post('api/admin/child-admin-login', null, params).then(res => {
                this.handleParentChildLoginResponse(res);
            }, function (err) {
                return err
            });
        },
        childToParentLogin: function () {
            return ApiService.post('api/admin/back-to-parent').then(res => {
                this.handleParentChildLoginResponse(res);
            }, function (err) {
                return err
            });
        },
        handleParentChildLoginResponse: function (response) {
            if (response.status === 200) {
                const userData = response.data;
                authService.setUserInfo(userData).then(() => {
                    utilService.nextScreen();
                });
            }

            return response;
        },
        existSessionHandler: (email, password, error) => {
            const { title, message } = error.data;

            const onSuccess = () => {
                const params = { deleteOtherSessions: true };
                authService.signIn(email, password, false, params);
            };

            ModalService.confirm(title, onSuccess, null, message, 'OK', 'Force Login');
        },
        setUserInfo: setUserInfo,
        clearUserInfo: clearUserInfo,
    };

    return authService;
}

